/**
 *
 * richJson lib
 *
 * JSON.serialize and JSON.parse don't support BigInt values.
 * To address this limitation, we have created this small library that adds support for BigInt numbers
 * during JSON serialization and parsing.
 *
 * Limitations: The library does not currently support BigInt values in new Map, Set, or Uint8Array.
 * However, extending and adding support can be easily accomplished if needed.
 * @credits: https://dev.to/benlesh/bigint-and-json-stringify-json-parse-2m8p
 *
 *
 * Additionally, JSON.serialize and JSON.parse do not properly serialize the Error object, so we extend that functionality here as well.
 *
 * Finally, the serializer is cycle-safe: a circular reference is replaced with
 * the "[Circular]" marker instead of throwing. Native `JSON.stringify` throws on
 * any cyclic structure ("Converting circular structure to JSON" in V8, "JSON.stringify
 * cannot serialize cyclic structures" in JavaScriptCore/Safari). This matters because
 * `stringify` is the transport for every controller-state dispatch on the mobile-web
 * worker and the extension port messenger: a controller can hold a cyclic object in its
 * `toJSON()` — most commonly a raw ethers network/server error (whose `request` ↔
 * `response` reference each other) that landed in `emittedErrors` after an RPC failure.
 * Without this guard a single such error crashes the entire state dispatch, and on
 * iOS Safari surfaced as a "cyclic structures" banner on the unlock screen. See the
 * WalletConnectSigner.toJSON workaround for a hand-patched instance of the same class.
 */

interface Options {
  pretty?: boolean
}

export function stringify(obj: any, opts?: Options): string {
  // Ancestor-path cycle guard (json-stringify-safe algorithm): `stack` holds the
  // chain of objects from the root down to the current holder. Only a value that
  // is its own ancestor is a true cycle; shared references that are merely
  // siblings (a DAG, e.g. the same token object in two lists) are preserved.
  const stack: any[] = []
  const keys: string[] = []

  return JSON.stringify(
    obj,
    function replacer(this: any, key, value) {
      if (stack.length > 0) {
        const thisPos = stack.indexOf(this)
        if (thisPos !== -1) {
          stack.splice(thisPos + 1)
          keys.splice(thisPos + 1)
        } else {
          stack.push(this)
          keys.push(key)
        }
        if (stack.indexOf(value) !== -1) return '[Circular]'
      } else {
        stack.push(value)
      }

      if (typeof value === 'bigint') {
        return { $bigint: value.toString() }
      }

      if (value instanceof Error) {
        const error: any = {}

        Object.getOwnPropertyNames(value).forEach((propName) => {
          // @ts-ignore
          error[propName] = value[propName]
        })

        return error
      }

      return value
    },
    opts?.pretty ? 4 : 0
  )
}

export function parse(json: string) {
  return JSON.parse(json, (key, value) => {
    if (value?.$bigint) {
      return BigInt(value.$bigint)
    }

    if (value?.stack?.startsWith('Error')) {
      const error = new Error(value.message)
      Object.getOwnPropertyNames(value).forEach((propName) => {
        if (propName !== 'message') {
          // @ts-ignore
          error[propName] = value[propName]
        }
      })

      return error
    }

    return value
  })
}

export function cloneDeep(value: any): any {
  if (value === null || typeof value !== 'object') {
    if (typeof value === 'function' || typeof value === 'symbol' || value === undefined) {
      return undefined
    }
    return value
  }

  if (value instanceof Error) {
    const error: any = new Error(value.message)
    Object.getOwnPropertyNames(value).forEach((propName) => {
      if (propName !== 'message') {
        error[propName] = (value as any)[propName]
      }
    })
    return error
  }

  if (Array.isArray(value)) {
    return value.map((item) => {
      const cloned = cloneDeep(item)
      // JSON.stringify turns undefined/functions in arrays into null
      return cloned === undefined ? null : cloned
    })
  }

  const objToClone = typeof value.toJSON === 'function' ? value.toJSON() : value

  // If toJSON returned a primitive, return it
  if (objToClone === null || typeof objToClone !== 'object') {
    return objToClone
  }

  const clone: any = {}
  for (const key in objToClone) {
    if (Object.prototype.hasOwnProperty.call(objToClone, key) && typeof key !== 'symbol') {
      const clonedVal = cloneDeep(objToClone[key])
      if (clonedVal !== undefined) {
        clone[key] = clonedVal
      }
    }
  }
  return clone
}
